import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import ShareMenu from '../components/ShareMenu';
import { slugify } from '../mockData';
import { useGallery, useAntiques, useArtists } from '../lib/usePublicData';
import { useT, localize } from '../lib/i18n';

// Uniform slug for an item — title + secondary key (artist for gallery, period for annex).
const itemSlug = (item, secondary) => {
  const base = [item.title, secondary].filter(Boolean).join(' ');
  return slugify(base);
};

/**
 * Individual artwork/antique detail page.
 * Route: /piece/:kind/:slug  (kind = 'gallery' | 'annex')
 *
 * Emits JSON-LD structured data so Google can serve rich results (image + price + availability).
 */
const ArtworkDetail = () => {
  const { kind, slug } = useParams();
  const { t, lang } = useT();
  const { items: gallery } = useGallery();
  const { items: antiques } = useAntiques();
  const { items: artists } = useArtists();
  const [showContext, setShowContext] = useState(false);

  const source = kind === 'annex' ? antiques : gallery;
  const item = useMemo(() => {
    if (!source?.length) return null;
    return source.find((x) => {
      const secondary = kind === 'annex' ? x.period : x.artist;
      return itemSlug(x, secondary) === slug;
    });
  }, [source, slug, kind]);

  const artist = useMemo(() => {
    if (!item || kind !== 'gallery') return null;
    return artists.find((a) => (a.name || '').trim() === (item.artist || '').trim()) || null;
  }, [item, artists, kind]);

  // Update document title dynamically for SEO
  useEffect(() => {
    if (!item) return;
    const secondary = kind === 'annex' ? item.period : item.artist;
    const dt = localize(item, 'title', lang);
    document.title = `${dt}${secondary ? ' — ' + secondary : ''} | The Chimera Gallery`;
    return () => { document.title = 'The Chimera Gallery — Contemporary Art & Fine Antiques'; };
  }, [item, kind, lang]);

  if (source?.length && !item) return <Navigate to={kind === 'annex' ? '/annex' : '/gallery'} replace />;
  if (!item) return null;

  const secondary = kind === 'annex' ? item.period : item.artist;
  const dispTitle = localize(item, 'title', lang);
  const dispDesc = localize(item, 'description', lang);
  const priceNumeric = (item.price || '').toString().replace(/[^\d.]/g, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://chimera-gallery.com';
  const pieceUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareUrl = `${origin}/api/share/${kind}/${slug}`;

  // JSON-LD structured data — this is what unlocks rich results in Google
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: dispTitle,
    description: dispDesc || `${dispTitle}${secondary ? ' — ' + secondary : ''}`,
    image: item.image ? `https://chimera-gallery.com${item.image}` : undefined,
    ...(kind === 'gallery' && item.artist ? {
      brand: { '@type': 'Person', name: item.artist },
      creator: { '@type': 'Person', name: item.artist },
    } : {}),
    ...(priceNumeric ? {
      offers: {
        '@type': 'Offer',
        priceCurrency: 'EUR',
        price: priceNumeric,
        availability: item.sold ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
        url: pieceUrl,
      },
    } : {}),
    ...(item.medium ? { material: item.medium } : {}),
    ...(item.size ? { size: item.size } : {}),
  };

  const backHref = kind === 'annex' ? '/annex' : '/gallery';
  const inquireSubject = encodeURIComponent(`${t('common.inquireSubject')} ${item.title}${secondary ? ' — ' + secondary : ''}`);

  return (
    <div className="min-h-screen bg-cream" data-testid="artwork-detail">
      {/* Inline JSON-LD for search engines */}
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="max-w-6xl mx-auto px-6 py-16">
        <Link
          to={backHref}
          className="inline-flex items-center gap-2 text-dark-green hover:text-dark-crimson mb-8 tracking-wider text-sm"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
          data-testid="back-link"
        >
          <ArrowLeft size={18} />
          {kind === 'annex' ? t('nav.annex') : t('nav.gallery')}
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Image side */}
          <div>
            <div
              className="relative aspect-[4/5] bg-ivory overflow-hidden border-2 border-gold/40 group cursor-pointer"
              onClick={() => item.imageContext && setShowContext((v) => !v)}
              data-testid="artwork-image"
            >
              {item.image && (
                <img
                  src={item.image}
                  alt={item.title}
                  loading="eager"
                  className={`absolute inset-0 w-full h-full object-contain p-6 transition-opacity duration-500 ${showContext ? 'opacity-0' : 'opacity-100'}`}
                />
              )}
              {item.imageContext && (
                <img
                  src={item.imageContext}
                  alt={`${item.title} — in situ`}
                  loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showContext ? 'opacity-100' : 'opacity-0'}`}
                />
              )}
            </div>
            {item.imageContext && (
              <p className="mt-3 text-xs text-dark-green/60 text-center tracking-wider italic">
                Click image to toggle in-situ view
              </p>
            )}
          </div>

          {/* Info side */}
          <div>
            <h1 className="text-5xl font-bold text-dark-green mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {dispTitle}
            </h1>

            {kind === 'gallery' && item.artist && (
              artist ? (
                <Link
                  to={`/artists/${artist.slug}`}
                  className="text-xl text-dark-crimson hover:text-crimson italic block mb-6"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid="artist-link"
                >
                  {item.artist} →
                </Link>
              ) : (
                <p className="text-xl text-dark-crimson italic mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {item.artist}
                </p>
              )
            )}

            {kind === 'annex' && item.period && (
              <p className="text-xl text-dark-crimson italic mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {item.period}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4 py-4 border-y-2 border-gold/30 my-6 text-sm"
                 style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {item.medium && (<div><span className="text-dark-green/60 uppercase tracking-wider text-xs">Medium</span><div className="text-dark-green mt-1">{item.medium}</div></div>)}
              {item.size && (<div><span className="text-dark-green/60 uppercase tracking-wider text-xs">Size</span><div className="text-dark-green mt-1">{item.size}</div></div>)}
              {item.year && (<div><span className="text-dark-green/60 uppercase tracking-wider text-xs">Year</span><div className="text-dark-green mt-1">{item.year}</div></div>)}
              {item.condition && (<div><span className="text-dark-green/60 uppercase tracking-wider text-xs">Condition</span><div className="text-dark-green mt-1">{item.condition}</div></div>)}
            </div>

            {item.description && (
              <p className="text-charcoal leading-relaxed mb-8 whitespace-pre-line"
                 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem' }}>
                {dispDesc}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-6 border-t-2 border-gold/30">
              {item.price && (
                <span className="text-3xl font-bold text-gold" data-testid="artwork-price">
                  {item.sold ? <span className="line-through opacity-60 mr-3">{item.price}</span> : item.price}
                  {item.sold && <span className="text-dark-crimson text-lg tracking-wider ml-2">SOLD</span>}
                </span>
              )}

              {!item.sold && (
                <Link to={`/contact?subject=${inquireSubject}`} data-testid="detail-inquire">
                  <Button className="bg-dark-crimson hover:bg-crimson text-cream px-8 py-4 rounded-none border border-gold tracking-wider">
                    <Mail className="mr-2" size={16} />
                    {kind === 'annex' ? t('common.requestInformation') : t('common.inquire')}
                  </Button>
                </Link>
              )}

              <ShareMenu url={shareUrl} title={`${dispTitle}${secondary ? ' — ' + secondary : ''} · The Chimera Gallery`} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArtworkDetail;
